import React, { useState } from 'react';
import { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import NextIcon from 'assets/actionPlan/next.svg';
import PrevIcon from 'assets/actionPlan/prev.svg';
import SaveIcon from 'assets/actionPlan/save.svg';
import Button from 'common-ui/button';
import dayjs from 'dayjs';

import {
  getAssignmentById,
  updateAssignment,
} from '../../../features/actions/actionPlanActions';
import {
  ACTION_OPTIONS,
  ASSIGNEE_OPTIONS,
  DURATION_TYPE_OPTIONS,
  ESTIMATION_OPTIONS,
  STATUS_OPTIONS,
} from '../ActionPlanConstans';
import {
  FooterContainer,
  FooterContainer1,
  Icon,
  Step,
  StepDivider,
  StepLabel,
  StepNumber,
  StepperContainer1,
} from '../actionAssignment/ActionAssignment.styles';
import { assignmentSchema } from '../validationSchemas';
import { Container1, FormGrid, Title } from './EmployeeSteps.styles';
import { Step1ActionDate } from './steps';
import Step3KpiConfig from './steps/Step3KpiConfig';
import Step4Compensation from './steps/Step4Compensation';

const initialState = {
  effectiveDate: null,
  actionType: null,
  estimationType: null,
  kpiConfig: {
    duration: null,
    currency: { value: 'AMD', label: 'AMD' },
    qLowAmount: '',
    qTargetAmount: '',
    qHighAmount: '',
    lowAmount: '',
    targetAmount: '',
    highAmount: '',
  },
  compensationConfig: {
    belowLow: '',
    lowToTarget: '',
    targetToHigh: '',
    aboveHigh: '',
  },
};

const steps = [
  {
    id: 1,
    label: 'Configure KPI',
  },
  {
    id: 2,
    label: 'Compensation',
  },
];
const formatOptionLabel = (value) =>
  value
    ?.toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapResponseToForm = (res) => {
  return {
    effectiveDate: res.effectiveDate,

    actionType: res.actionType
      ? {
          value: res.actionType.toLowerCase(),
          label: formatOptionLabel(res.actionType),
        }
      : null,

    estimationType: res.estimationType
      ? {
          value: res.estimationType.toLowerCase(),
          label: formatOptionLabel(res.estimationType),
        }
      : null,

    kpiConfig: {
      ...initialState.kpiConfig,

      duration: res.kpiConfig?.periodType
        ? {
            value: res.kpiConfig.periodType.toLowerCase(),
            label: formatOptionLabel(res.kpiConfig.periodType),
          }
        : null,

      currency: res.kpiConfig?.currency
        ? {
            value: res.kpiConfig.currency,
            label: res.kpiConfig.currency,
          }
        : { value: 'AMD', label: 'AMD' },

      qLowAmount: res.kpiConfig?.quantityLow ?? '',
      qTargetAmount: res.kpiConfig?.quantityTarget ?? '',
      qHighAmount: res.kpiConfig?.quantityHigh ?? '',

      lowAmount: res.kpiConfig?.financialLow ?? '',
      targetAmount: res.kpiConfig?.financialTarget ?? '',
      highAmount: res.kpiConfig?.financialHigh ?? '',
    },

    compensationConfig: {
      belowLow: res.compensationConfig?.belowLowDeduction,
      lowToTarget: res.compensationConfig?.lowToTargetDeduction,
      targetToHigh: res.compensationConfig?.targetToHighBonus,
      aboveHigh: res.compensationConfig?.aboveHighBonus,
    },
  };
};
const EditAssignment = () => {
  const buildPayload = () => {
    const estType = formData.estimationType?.value;

    return {
      effectiveDate: formData.effectiveDate
        ? dayjs(formData.effectiveDate).format('YYYY-MM-DD')
        : null,

      actionType: formData.actionType?.value?.toUpperCase(),
      estimationType: estType?.toUpperCase(),

      kpiConfig: {
        periodType: formData.kpiConfig?.duration?.value?.toUpperCase(),

        quantityLow: estType !== 'financial' ? Number(formData.kpiConfig.qLowAmount || 0) : 0,
        quantityTarget:
          estType !== 'financial' ? Number(formData.kpiConfig.qTargetAmount || 0) : 0,
        quantityHigh:
          estType !== 'financial' ? Number(formData.kpiConfig.qHighAmount || 0) : 0,

        financialLow: estType !== 'quantity' ? Number(formData.kpiConfig.lowAmount || 0) : 0,
        financialTarget:
          estType !== 'quantity' ? Number(formData.kpiConfig.targetAmount || 0) : 0,
        financialHigh: estType !== 'quantity' ? Number(formData.kpiConfig.highAmount || 0) : 0,

        currency: formData.kpiConfig.currency?.value || 'AMD',
      },

      ...(!isDepartmentAssignment && {
        compensationConfig: {
          belowLowDeduction: Number(formData.compensationConfig.belowLow || 0),
          lowToTargetDeduction: Number(formData.compensationConfig.lowToTarget || 0),
          targetToHighBonus: Number(formData.compensationConfig.targetToHigh || 0),
          aboveHighBonus: Number(formData.compensationConfig.aboveHigh || 0),
        },
      }),
      ...(!isDepartmentAssignment &&
        estType !== 'quantity' && {
          compensationConfig: {
            belowLowDeduction: Number(formData.compensationConfig.belowLow || 0),
            lowToTargetDeduction: Number(formData.compensationConfig.lowToTarget || 0),
            targetToHighBonus: Number(formData.compensationConfig.targetToHigh || 0),
            aboveHighBonus: Number(formData.compensationConfig.aboveHigh || 0),
          },
        }),
    };
  };
  const { id: assignmentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const isEditMode = !!assignmentId;
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting] = useState(false);
  const [isSaved] = useState(false);

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const isReadOnly = false;
  const isDepartmentAssignment = data?.scope === 'DEPARTMENT';

  const visibleSteps = isDepartmentAssignment
    ? [{ id: 1, label: 'Configure KPI' }]
    : [
        { id: 1, label: 'Configure KPI' },
        { id: 2, label: 'Compensation' },
      ];

  const isLastStep = currentStep === visibleSteps.length;

  const tomorrow = dayjs().add(1, 'day').startOf('day');

  const actionTypeOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
  ];

  const estimationTypeOptions = [
    { value: 'quantity', label: 'Quantity' },
    { value: 'financial', label: 'Financial' },
    { value: 'both', label: 'Both' },
  ];

  const validateCurrentStep = async () => {
    const estType = formData.estimationType?.value;

    let fields = [];

    if (currentStep === 1) {
      fields = [
        'effectiveDate',
        'actionType',
        'estimationType',
        'kpiConfig.duration',
        'kpiConfig.currency',
      ];

      if (estType === 'quantity' || estType === 'both') {
        fields.push(
          'kpiConfig.qLowAmount',
          'kpiConfig.qTargetAmount',
          'kpiConfig.qHighAmount'
        );
      }

      if (estType === 'financial' || estType === 'both') {
        fields.push('kpiConfig.lowAmount', 'kpiConfig.targetAmount', 'kpiConfig.highAmount');
      }
    }

    if (currentStep === 2) {
      fields = [
        'compensationConfig.belowLow',
        'compensationConfig.lowToTarget',
        'compensationConfig.targetToHigh',
        'compensationConfig.aboveHigh',
      ];
    }

    const validationErrors = {};

    await Promise.all(
      fields.map(async (field) => {
        try {
          await assignmentSchema.validateAt(field, formData, {
            context: {
              estType,
              assignmentType: isDepartmentAssignment ? 'department' : 'employee',
            },
          });
        } catch (err) {
          validationErrors[field] = err.message;
        }
      })
    );

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };
  const handleNext = async () => {
    setSubmitted(true);

    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (currentStep < visibleSteps.length) {
      setCurrentStep((prev) => prev + 1);
      setSubmitted(false);
      setErrors({});
      return;
    }

    const payload = buildPayload();

    const scope = data?.scope === 'EMPLOYEE' ? 'employee' : 'department';

    await dispatch(
      updateAssignment({
        id: assignmentId,
        scope,
        assignmentData: payload,
      })
    ).unwrap();

    navigate(-1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleFieldChange = async (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };

    setFormData(newFormData);

    if (!submitted) return;

    try {
      await assignmentSchema.validateAt(field, newFormData, {
        context: {
          estType: newFormData.estimationType?.value,
          assignmentType: isDepartmentAssignment ? 'department' : 'employee',
        },
      });

      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [field]: err.message,
      }));
    }
  };

  const handleKpiChange = async (field, value) => {
    const newFormData = {
      ...formData,
      kpiConfig: {
        ...formData.kpiConfig,
        [field]: value,
      },
    };

    setFormData(newFormData);

    if (!submitted) return;

    let fieldsToValidate = [`kpiConfig.${field}`];

    if (['lowAmount', 'targetAmount', 'highAmount'].includes(field)) {
      fieldsToValidate = [
        'kpiConfig.lowAmount',
        'kpiConfig.targetAmount',
        'kpiConfig.highAmount',
      ];
    }

    if (['qLowAmount', 'qTargetAmount', 'qHighAmount'].includes(field)) {
      fieldsToValidate = [
        'kpiConfig.qLowAmount',
        'kpiConfig.qTargetAmount',
        'kpiConfig.qHighAmount',
      ];
    }

    const updatedErrors = { ...errors };

    for (const fieldPath of fieldsToValidate) {
      try {
        await assignmentSchema.validateAt(fieldPath, newFormData, {
          context: {
            estType: newFormData.estimationType?.value,
            assignmentType: isDepartmentAssignment ? 'department' : 'employee',
          },
        });

        delete updatedErrors[fieldPath];
      } catch (err) {
        updatedErrors[fieldPath] = err.message;
      }
    }

    setErrors(updatedErrors);
  };

  const handleCompChange = async (field, value) => {
    const newFormData = {
      ...formData,
      compensationConfig: {
        ...formData.compensationConfig,
        [field]: value,
      },
    };

    setFormData(newFormData);

    if (!submitted) return;

    const fieldPath = `compensationConfig.${field}`;

    try {
      await assignmentSchema.validateAt(fieldPath, newFormData, {
        context: {
          estType: newFormData.estimationType?.value,
          assignmentType: isDepartmentAssignment ? 'department' : 'employee',
        },
      });

      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[fieldPath];
        return copy;
      });
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [fieldPath]: err.message,
      }));
    }
  };

  const estType = formData.estimationType?.value;
  const showQuantity = estType === 'quantity' || estType === 'both';

  const showFinancial = estType === 'financial' || estType === 'both';

  useEffect(() => {
    console.log('🔥 USEEFFECT TRIGGERED');
    console.log('👉 assignmentId:', assignmentId);
    if (!assignmentId) return;

    dispatch(getAssignmentById(assignmentId))
      .unwrap()
      .then((res) => {
        if (!res) return;

        setFormData(mapResponseToForm(res));
        setData(res);
      })
      .catch((err) => console.log('API ERROR:', err));
  }, [assignmentId, dispatch]);
  return (
    <>
      <StepperContainer1>
        {visibleSteps.map((item, index) => (
          <React.Fragment key={item.id}>
            <Step $active={currentStep === item.id} $completed={currentStep > item.id}>
              <StepNumber $active={currentStep === item.id}>{item.id}</StepNumber>

              <StepLabel $active={currentStep === item.id}>{item.label}</StepLabel>
            </Step>

            {index < visibleSteps.length - 1 && <StepDivider />}
          </React.Fragment>
        ))}
      </StepperContainer1>
      {currentStep === 1 && (
        <>
          <Container1>
            <Title>Configure KPI Thresholds</Title>
            <FormGrid>
              <Step1ActionDate
                formData={formData}
                errors={errors}
                submitted={submitted}
                isReadOnly={isReadOnly}
                handleFieldChange={handleFieldChange}
                handleKpiChange={handleKpiChange}
                hideAction
                showDuration={true}
              />
            </FormGrid>

            {currentStep === 1 && (
              <Step3KpiConfig
                formData={formData}
                errors={errors}
                submitted={submitted}
                isReadOnly={isReadOnly}
                handleKpiChange={handleKpiChange}
                showQuantityProps={true}
                showDuration={false}
                showTitle={false}
              />
            )}
          </Container1>
        </>
      )}

      {currentStep === 2 && !isDepartmentAssignment && (
        <Step4Compensation
          formData={formData}
          errors={errors}
          submitted={submitted}
          isReadOnly={isReadOnly}
          handleCompChange={handleCompChange}
        />
      )}

      <FooterContainer1 $isLastStep={isLastStep}>
        <div style={{ width: 100 }}>
          {currentStep !== 1 ? (
            <Button
              outlined
              onClick={handleBack}
              icon={PrevIcon}
              disabled={isSaved || isSubmitting}
            >
              Back
            </Button>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            outlined
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            style={{ width: '185px' }}
          >
            Close
          </Button>

          <Button
            primary
            onClick={handleNext}
            loading={isSubmitting}
            disabled={isSubmitting || isSaved}
          >
            {isLastStep ? 'Save Assignment' : 'Next'}

            <Icon src={isLastStep ? SaveIcon : NextIcon} $isLastStep={isLastStep} />
          </Button>
        </div>
      </FooterContainer1>
    </>
  );
};

export default EditAssignment;
